import React, { useState, useEffect } from 'react';
import { fetchPolygonSnapshotPrice } from '../composables/fetchPolygon';
import AverageCalculatePopup from './AverageCalculatePopup';
import toast from 'react-hot-toast';
import { getProgressBarColor } from '../composables/colorUtils';
import { usePortfolio } from '../context/PortfolioContext';
import { isCryptoSymbol } from '../composables/fetchCrypto';
import { holdingQuoteSymbol } from '../utils/portfolioType';
import { formatCryptoUsd, num } from '../utils/cryptoDecimal';
interface AverageCalculateProps {
  symbol: string;
  portfolioId: string;
  subPortId: string;
  avgBuyPrice?: number | null;
  updateTrigger: boolean;
  currentPrices: { [symbol: string]: number }; // เพิ่ม currentPrices
}

const AverageCalculate: React.FC<AverageCalculateProps> = ({ symbol, portfolioId, subPortId, avgBuyPrice, updateTrigger, currentPrices }) => {
  const [invest, setInvest] = useState(0);
  // const [currentPrice, setCurrentPrice] = useState(0);
  const [currentShares, setCurrentShares] = useState(0);
  const [currentCost, setCurrentCost] = useState(0);
  const [currentExpectRatio, setCurrentExpectRatio] = useState(0);
  const [useCustomPrice, setUseCustomPrice] = useState(false); // State for checkbox
  const [usePercentage, setUsePercentage] = useState(false); // State for checkbox
  const [useTargetPrice, setUseTargetPrice] = useState(false); // State for checkbox
  const [customPrice, setCustomPrice] = useState<number | null>(null); // State for custom price
  const [customPercentage, setCustomPercentage] = useState<number | null>(null); // State for custom percentage
  const [showPopup, setShowPopup] = useState(false);
  const [totalValueInSubPort, setTotalValueInSubPort] = useState(0); // มูลค่ารวมของพอร์ตย่อย
  const [targetPrice, setTargetPrice] = useState<number | null>(null); // ราคาที่อยากได้
  const [targetRatio, setTargetRatio] = useState<number | null>(null); // เป้าหมาย %
  const [requiredShares, setRequiredShares] = useState(0); // จำนวนหุ้นที่ต้องซื้อ
  const [requiredInvestment, setRequiredInvestment] = useState(0); // เงินที่ต้องใช้
  const [newAvgPrice, setNewAvgPrice] = useState(0); // ค่าเฉลี่ยใหม่
  const [initialAvgPrice, setInitialAvgPrice] = useState<number | null>(null); // เก็บค่าเฉลี่ยเริ่มต้น
  const [updatedCurrentRatio, setUpdatedCurrentRatio] = useState<number | null>(null);
  const { userPortfolios } = usePortfolio();

  // คริปโต: จำนวนเหรียญเป็นเศษได้มาก / ราคาเหรียญถูกต่ำกว่า $1 ได้ → ใช้ทศนิยมมากกว่าหุ้น (แสดงผลเท่านั้น)
  const isCrypto = isCryptoSymbol(symbol);
  const unit = isCrypto ? 'เหรียญ' : 'หุ้น';
  const usd = (n: number) => (isCrypto ? formatCryptoUsd(n) : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const qty = (n: number, stockDigits = 2) =>
    isCrypto ? n.toLocaleString(undefined, { maximumFractionDigits: 8 }) : n.toLocaleString(undefined, { minimumFractionDigits: stockDigits, maximumFractionDigits: stockDigits });

  // คำนวณราคาเฉลี่ยใหม่
  const currentPrice = currentPrices[symbol] || 0; // ใช้ currentPrices จาก props
  const effectivePrice = useCustomPrice && customPrice !== null ? customPrice : currentPrice; // Use custom price if checkbox is checked
  const additionalShares = effectivePrice > 0 ? invest / effectivePrice : 0;
  const newShares = useTargetPrice
    ? currentShares + requiredShares // ใช้จำนวนหุ้นที่ต้องซื้อเพิ่ม
    : usePercentage
      ? currentShares + requiredShares // ใช้จำนวนหุ้นที่ต้องซื้อเพิ่ม
      : currentShares + additionalShares; // ใช้จำนวนหุ้นที่คำนวณจากเงินลงทุน

  const newCost = useTargetPrice
    ? currentCost + requiredInvestment // ใช้เงินที่ต้องใช้จากการคำนวณราคาเป้าหมาย
    : usePercentage
      ? currentCost + requiredInvestment // ใช้เงินที่ต้องใช้จากการคำนวณเปอร์เซนต์
      : currentCost + invest; // ใช้เงินลงทุนที่กรอกเอง

  // const newAvgPrice = newShares > 0 ? newCost / newShares : 0;
  useEffect(() => {
    if (newShares > 0) {
      setNewAvgPrice(newCost / newShares);

      // toast.success(`New Average Price: ${(newCost / newShares).toFixed(2)} USD`);
    }
  }, [newShares, newCost]);

  // คำนวณมูลค่าปัจจุบันของหุ้น
  const currentValue = currentShares * currentPrice;

  // คำนวณสัดส่วนปัจจุบัน
  const calculatedCurrentRatio = totalValueInSubPort > 0 ? (currentValue / totalValueInSubPort) * 100 : 0;

  // คำนวณสัดส่วนใหม่หลังจากลงทุนเพิ่ม
  const newValue = newShares * effectivePrice;
  const newRatio = totalValueInSubPort > 0 ? (newValue / totalValueInSubPort) * 100 : 0;
  // useEffect(() => {
  //   toast.success(`currentPrice: ${currentPrice}`);
  //   toast.success(`currentShares: ${currentShares}`);
  //   toast.success(`totalValueInSubPort: ${totalValueInSubPort}`);
  //   toast.success(`targetRatio: ${targetRatio}`);
  //     toast.success(`currentValue: ${currentValue}`);

  // }, [currentPrice, currentShares, totalValueInSubPort, targetRatio,currentValue]);
  const handleTogglePercentage = () => {
    setUsePercentage((prev) => {
      if (!prev) {
        setUseTargetPrice(false); // ปิดตัวเลือก "ราคาเป้าหมายที่อยากได้" หากเปิด "คำนวณเป็นเปอร์เซนต์"
      }
      return !prev;
    });
  };

  const handleToggleTargetPrice = () => {
    setUseTargetPrice((prev) => {
      if (!prev) {
        setUsePercentage(false); // ปิดตัวเลือก "คำนวณเป็นเปอร์เซนต์" หากเปิด "ราคาเป้าหมายที่อยากได้"
      }
      return !prev;
    });
  };

  useEffect(() => {
    setCustomPrice(null);
    setCustomPercentage(null);
    setTargetPrice(null);
  }, [symbol]);

  useEffect(() => {
    if (useTargetPrice && targetPrice !== null && targetPrice > 0) {
      const priceToUse = useCustomPrice && customPrice !== null ? customPrice : currentPrice; // ใช้ customPrice ถ้ากำหนดเอง
      const minPrice = Math.min(priceToUse, newAvgPrice);
      const maxPrice = Math.max(priceToUse, newAvgPrice);
      // เพิ่มค่าความคลาดเคลื่อน — คริปโตราคาต่างกันหลายหลัก (DOGE $0.1 / BTC $80k) จึงคิดเป็นสัดส่วนของราคาแทน $1
      const tolerance = isCrypto ? Math.max(priceToUse, newAvgPrice) * 0.0001 : 1;

      // ตรวจสอบว่า targetPrice อยู่ในช่วงที่กำหนด (เพิ่ม tolerance)
      if (targetPrice >= minPrice - tolerance && targetPrice <= maxPrice + tolerance) {
        const denominator = targetPrice - priceToUse;

        // ตรวจสอบว่า denominator ไม่ใกล้ 0 เกินไป
        if (Math.abs(denominator) > tolerance) {
          const sharesToBuy = (currentCost - targetPrice * currentShares) / denominator;
          const investmentNeeded = sharesToBuy * priceToUse;

          if (sharesToBuy > 0) {
            setRequiredShares(sharesToBuy);
            setRequiredInvestment(investmentNeeded);

            // อัปเดตสัดส่วนปัจจุบันใหม่
            const newTotalShares = currentShares + sharesToBuy;
            const newTotalValue = newTotalShares * targetPrice;
            const newRatio = totalValueInSubPort > 0 ? (newTotalValue / totalValueInSubPort) * 100 : 0;

            // Debugging: ตรวจสอบค่าที่เกี่ยวข้อง
            // console.log("Target Price:", targetPrice);
            // console.log("Price to Use:", priceToUse);
            // console.log("New Avg Price:", newAvgPrice);
            // console.log("Denominator:", denominator);
            // console.log("New Ratio:", newRatio);

            // ตรวจสอบว่าค่าใหม่แตกต่างจากค่าเดิมก่อนอัปเดต
            if (updatedCurrentRatio !== newRatio) {
              setUpdatedCurrentRatio(newRatio);
            }
          } else {
            setRequiredShares(0);
            setRequiredInvestment(0);
            setUpdatedCurrentRatio(null);
          }
        } else {
          // หาก denominator ใกล้ 0 เกินไป
          // console.log("Denominator is too small:", denominator);
          setRequiredShares(0);
          setRequiredInvestment(0);
          setUpdatedCurrentRatio(null);
        }
      } else {
        // console.log("Target price out of range:", targetPrice);
        setUpdatedCurrentRatio(null); // คืนค่ากลับไปที่ค่าเดิม
        setRequiredShares(0);
        setRequiredInvestment(0);
      }
    } else {
      setUpdatedCurrentRatio(null); // คืนค่ากลับไปที่ค่าเดิม
      setRequiredShares(0);
      setRequiredInvestment(0);
    }
  }, [
    useTargetPrice,
    targetPrice,
    currentPrice,
    currentShares,
    currentCost,
    useCustomPrice,
    customPrice,
    totalValueInSubPort,
    // newAvgPrice,
    updatedCurrentRatio,
  ]);

  useEffect(() => {
    if (customPercentage !== null && customPercentage > calculatedCurrentRatio && totalValueInSubPort > 0) {
      const targetFraction = customPercentage / 100;
      const buyPrice = useCustomPrice && customPrice !== null ? customPrice : currentPrice;
      const marketPrice = currentPrice;

      if (buyPrice > 0 && marketPrice > 0) {
        const numerator = (targetFraction * totalValueInSubPort) - currentValue;
        const denominator = (marketPrice / buyPrice) - targetFraction;

        const investment = numerator / denominator;
        const shares = investment / buyPrice;

        if (investment > 0 && isFinite(investment)) {
          setRequiredShares(shares);
          setRequiredInvestment(investment);
          setNewAvgPrice((currentCost + investment) / (currentShares + shares));
        } else {
          setRequiredShares(0);
          setRequiredInvestment(0);
        }
      }
    } else {
      setRequiredShares(0);
      setRequiredInvestment(0);
    }
  }, [
    customPercentage,
    calculatedCurrentRatio,
    totalValueInSubPort,
    currentValue,
    useCustomPrice,
    customPrice,
    currentPrice,
  ]);

  useEffect(() => {
    if (!useCustomPrice) {
      setCustomPrice(currentPrice);
    }
  }, [currentPrice, useCustomPrice]);

  useEffect(() => {
    try {
      // 1. หา Portfolio หลัก
      const portfolio = userPortfolios.find((p: any) => (p.portfolioId || p.portId || p.id) === portfolioId);
      // 2. หา SubPort
      const subPort = portfolio?.subPorts?.find((sp: any) => sp.subPortId === subPortId);
      // 3. ดึงรายการหุ้น (รองรับทั้ง V2 แบบ holdings และ V1 แบบ stocks)
      const holdings = subPort?.holdings || subPort?.stocks || [];
      // 4. หาหุ้นตัวที่กำลังดูอยู่
      const stockInfo = holdings.find((s: any) => holdingQuoteSymbol(s) === symbol);

      if (stockInfo) {
        // อัปเดตข้อมูลหุ้น — num(): คริปโตส่งเป็น string ถ้าไม่แปลง currentShares + x จะกลายเป็นต่อข้อความ
        const avgPrice = num(stockInfo.avgEntryPrice || stockInfo.avgBuyPrice || 0);

        setCurrentExpectRatio(num(stockInfo.expectedRatio || stockInfo.ratio || 0));
        setCurrentShares(num(stockInfo.quantity));
        setCurrentCost(num(stockInfo.quantity) * avgPrice);
        setInitialAvgPrice(avgPrice); // เก็บค่าเฉลี่ยเริ่มต้น

        // คำนวณมูลค่ารวมของพอร์ตย่อยจากราคาปัจจุบัน
        let totalValue = 0;
        for (const stock of holdings) {
          const sym = holdingQuoteSymbol(stock);
          const price = currentPrices?.[sym] || num(stock.avgEntryPrice || stock.avgBuyPrice || 0);

          const stockValue = num(stock.quantity) * price;
          totalValue += stockValue;
        }

        setTotalValueInSubPort(totalValue);
      } else {
        // กรณีหาหุ้นไม่เจอ (อาจจะเพิ่งขายทิ้งหมด หรือเป็นหุ้นใหม่)
        setCurrentExpectRatio(0);
        setCurrentShares(0);
        setCurrentCost(0);
        setTotalValueInSubPort(0);
      }
    } catch (error) {
      console.error('Error calculating portfolio from context:', error);
      setCurrentShares(0);
      setCurrentCost(0);
      setTotalValueInSubPort(0);
    }
  }, [symbol, portfolioId, subPortId, currentPrices, userPortfolios]);

  // useEffect(() => {
  //   if (useTargetPrice && targetPrice !== null && targetPrice > 0) {
  //     const priceToUse = useCustomPrice && customPrice !== null ? customPrice : currentPrice; // ใช้ customPrice ถ้ากำหนดเอง
  //     const denominator = targetPrice - priceToUse;

  //     // ตรวจสอบตัวหารไม่ให้เป็นศูนย์
  //     if (denominator !== 0) {
  //       const sharesToBuy = (currentCost - targetPrice * currentShares) / denominator;
  //       const investmentNeeded = sharesToBuy * priceToUse;

  //       if (sharesToBuy > 0) {
  //         setRequiredShares(sharesToBuy);
  //         setRequiredInvestment(investmentNeeded);
  //       } else {
  //         setRequiredShares(0);
  //         setRequiredInvestment(0);
  //       }
  //     } else {
  //       // กรณีที่ targetPrice เท่ากับ priceToUse
  //       setRequiredShares(0);
  //       setRequiredInvestment(0);
  //     }
  //   } else {
  //     setRequiredShares(0);
  //     setRequiredInvestment(0);
  //   }
  // }, [useTargetPrice, targetPrice, currentPrice, currentShares, currentCost, useCustomPrice, customPrice]);

  useEffect(() => {
    // คำนวณสัดส่วนใหม่แบบชั่วคราวเมื่อใส่เงินลงทุนเพิ่ม
    if (invest > 0) {
      const tempAdditionalShares = effectivePrice > 0 ? invest / effectivePrice : 0;
      const tempNewShares = currentShares + tempAdditionalShares;
      const tempNewCost = currentCost + invest;
      const tempNewValue = tempNewShares * effectivePrice;
      const tempNewRatio = totalValueInSubPort > 0 ? (tempNewValue / totalValueInSubPort) * 100 : 0;

      // setCurrentExpectRatio(tempNewRatio); // อัปเดตสัดส่วนใหม่แบบชั่วคราว
    } else {
      // setCurrentExpectRatio(calculatedCurrentRatio); // กลับไปใช้สัดส่วนเดิม
    }
  }, [invest, effectivePrice, currentShares, currentCost, totalValueInSubPort, calculatedCurrentRatio]);

  useEffect(() => {
    if (usePercentage && customPercentage !== null) {
      const targetFraction = customPercentage / 100;
      const buyPrice = useCustomPrice && customPrice !== null ? customPrice : currentPrice;
      const marketPrice = currentPrice;

      if (buyPrice > 0 && marketPrice > 0) {
        const numerator = targetFraction * totalValueInSubPort - currentValue;
        const denominator = marketPrice / buyPrice - targetFraction;

        if (denominator !== 0) {
          const investment = numerator / denominator;
          const shares = investment / buyPrice;

          if (investment > 0 && isFinite(investment)) {
            setRequiredShares(shares);
            setRequiredInvestment(investment);

            // อัปเดตค่าที่เกี่ยวข้อง
            const tempNewShares = currentShares + shares;
            const tempNewCost = currentCost + investment;
            setNewAvgPrice(tempNewCost / tempNewShares);
          } else {
            setRequiredShares(0);
            setRequiredInvestment(0);
          }
        } else {
          // console.log("Denominator is zero, cannot calculate investment.");
          setRequiredShares(0);
          setRequiredInvestment(0);
        }
      } else {
        // console.log("Buy price or market price is invalid.");
        setRequiredShares(0);
        setRequiredInvestment(0);
      }
    } else {
      // console.log("Custom percentage is invalid or less than the current ratio.");
      setRequiredShares(0);
      setRequiredInvestment(0);
    }
  }, [
    usePercentage,
    customPercentage,
    calculatedCurrentRatio,
    totalValueInSubPort,
    currentValue,
    useCustomPrice,
    customPrice,
    currentPrice,
  ]);
  // useEffect(() => {
  //   toast.success(`usePercentage: ${usePercentage}`);
  //   toast.success(`customPercentage: ${customPercentage}`);
  //   toast.success(`calculatedCurrentRatio: ${calculatedCurrentRatio}`);
  //   toast.success(`totalValueInSubPort: ${totalValueInSubPort}`);
  //   toast.success(`currentValue: ${currentValue}`);
  //   toast.success(`currentPrice: ${currentPrice}`);
  //   toast.success(`customPrice: ${customPrice}`);
  // }, [
  //   usePercentage,
  //   customPercentage,
  //   calculatedCurrentRatio,
  //   totalValueInSubPort,
  //   currentValue,
  //   currentPrice,
  //   customPrice,
  // ]);

  const calculateTemporaryRatio = (investment: number) => {
    let actualAddedInvestment = 0;
    let tempNewShares = currentShares;

    // 1. กรณีระบุ % เป้าหมาย
    if (usePercentage && customPercentage !== null && customPercentage >= calculatedCurrentRatio) {
      return customPercentage;
    }

    // 2. กรณีใส่ "ค่าเฉลี่ยใหม่ที่อยากได้"
    if (useTargetPrice && targetPrice !== null && targetPrice > 0) {
      const priceToUse = useCustomPrice && customPrice !== null ? customPrice : currentPrice;
      const minPrice = Math.min(priceToUse, initialAvgPrice || 0);
      const maxPrice = Math.max(priceToUse, initialAvgPrice || 0);
      const tolerance = 0.01;

      if (targetPrice >= minPrice - tolerance && targetPrice <= maxPrice + tolerance) {
        const denominator = targetPrice - priceToUse;

        if (Math.abs(denominator) > 0.0001) {
          const sharesToBuy = (currentCost - targetPrice * currentShares) / denominator;
          if (sharesToBuy > 0) {
            actualAddedInvestment = sharesToBuy * priceToUse;
            tempNewShares = currentShares + sharesToBuy;
          }
        }
      }
    }
    // 3. กรณี "ใส่เงินลงทุนเพิ่ม" (invest)
    else if (investment > 0) {
      actualAddedInvestment = investment;
      const tempAdditionalShares = effectivePrice > 0 ? investment / effectivePrice : 0;
      tempNewShares = currentShares + tempAdditionalShares;
    }

    // --- ส่วนคำนวณสัดส่วน (จุดที่แก้ไข) ---
    // if (actualAddedInvestment > 0 || tempNewShares > currentShares) {
    //   // เปลี่ยนมาใช้ "ต้นทุนรวมใหม่" (Cost Basis) เพื่อให้ตรงกับฐานเงินลงทุนที่เติมเข้าไป
    //   const newTotalCost = currentCost + actualAddedInvestment; 

    //   // ตัวหารคือ มูลค่าพอร์ตปัจจุบัน + เงินสดที่เติมเพิ่ม
    //   const newTotalPortValue = totalValueInSubPort + actualAddedInvestment;

    //   if (newTotalPortValue > 0) {
    //     const ratio = (newTotalCost / newTotalPortValue) * 100;
    //     return ratio > 100 ? 100 : ratio;
    //   }
    // }
    // แก้ไขส่วนนี้ในฟังก์ชัน calculateTemporaryRatio
    if (actualAddedInvestment > 0 || tempNewShares > currentShares) {
      // 1. คำนวณมูลค่าตลาดใหม่ของหุ้นตัวนี้ (หุ้นเดิม + หุ้นใหม่) * ราคาปัจจุบัน
      const newAssetValue = tempNewShares * effectivePrice;

      // 2. คำนวณมูลค่ารวมของพอร์ตใหม่ (มูลค่าพอร์ตเดิม + เงินที่เติมเข้าไปใหม่)
      const newTotalPortValue = totalValueInSubPort + actualAddedInvestment;

      if (newTotalPortValue > 0) {
        // 3. สัดส่วน = (มูลค่าตลาดใหม่ของหุ้น / มูลค่ารวมพอร์ตใหม่)
        const ratio = (newAssetValue / newTotalPortValue) * 100;
        return ratio > 100 ? 100 : ratio;
      }
    }
    return calculatedCurrentRatio;
  };
  return (
    <div
      className="w-full max-w-sm mx-auto px-6 rounded-lg shadow-lg"
      style={{ backgroundColor: 'var(--component-bg-color)', color: 'var(--text-color)' }}
    >
      {/* เกจ bar */}
      <div className="mb-1">
        <p className="text-sm mb-2" style={{ color: 'var(--text-color)' }}>
          สัดส่วนปัจจุบัน: {calculateTemporaryRatio(invest).toFixed(2)}% | เป้าหมาย: {currentExpectRatio.toFixed(2) || 0}%
        </p>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className="bg-green-500 h-2.5 rounded-full"
            style={{
              width: `${Math.min((calculateTemporaryRatio(invest) / currentExpectRatio) * 100, 100)}%`,
              backgroundColor: getProgressBarColor(
                (calculateTemporaryRatio(invest) / currentExpectRatio) * 100,
                currentExpectRatio
              ),
            }}          ></div>
        </div>
        {/* เพิ่มหมายเหตุสีเทา */}
        <p className="text-xs mt-2" style={{ color: 'gray' }}>
          หมายเหตุ: สัดส่วนอาจมีการคาดเคลื่อนตามความละเอียด
        </p>
      </div>





      {/* 
      <div className="rounded-lg p-4">
        <div className="flex justify-between mb-2">
          <span className="text-sm" style={{ color: 'var(--text-color)' }}>
            จำนวนหุ้นที่ต้องซื้อ:
          </span>
          <span className="font-bold" style={{ color: 'var(--text-color)' }}>
            {requiredShares.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm" style={{ color: 'var(--text-color)' }}>
            เงินที่ต้องใช้:
          </span>
          <span className="font-bold" style={{ color: 'var(--text-color)' }}>
            ${requiredInvestment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      </div> */}

      {/* <p
        className="text-sm mb-2"
        style={{ color: 'var(--text-color)' }}
      >
        ถ้าซื้อเพิ่มที่ราคาปัจจุบัน จะทำให้ต้นทุนเฉลี่ยของคุณเปลี่ยนไปอย่างไร
      </p> */}
      <div className="flex items-center mb-1">
        {/* กำหนดราคาเอง */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="useCustomPrice"
            className="mr-2"
            checked={useCustomPrice}
            onChange={(e) => setUseCustomPrice(e.target.checked)}
          />
          <label
            htmlFor="useCustomPrice"
            className="text-xs"
            style={{ color: 'var(--text-color)' }}
          >
            กำหนดราคาเอง($)
          </label>
        </div>

        {/* เส้นขั้น */}
        <div
          style={{
            borderLeft: '1px solid var(--text-color)',
            height: '50px',
            margin: '0 10px',
          }}
        ></div>

        {/* คำนวณเป็นเปอร์เซนต์ และ ราคาเป้าหมายที่อยากได้ */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="usePercentage"
            className="mr-2"
            checked={usePercentage}
            onChange={handleTogglePercentage}
          />
          <label
            htmlFor="usePercentage"
            className="text-xs"
            style={{ color: 'var(--text-color)' }}
          >
            คำนวณเป็นเปอร์เซนต์(%)
          </label>

          <input
            type="checkbox"
            id="useTargetPrice"
            className="mr-2"
            checked={useTargetPrice}
            onChange={handleToggleTargetPrice}
          />
          <label
            htmlFor="useTargetPrice"
            className="text-xs"
            style={{ color: 'var(--text-color)' }}
          >
            ค่าเฉลี่ยใหม่ที่อยากได้($)
          </label>
        </div>
      </div>



      {useCustomPrice && (
        <>
          <label
            className="block mb-2 text-sm"
            style={{ color: 'var(--text-color)' }}
          >
            กำหนดราคาตลาด <span style={{ color: 'var(--yellow-text-color)' }}>(USD)</span>
          </label>

          <div className="flex items-center mb-4">

            <span
              className="mr-2 font-bold"
              style={{ color: 'var(--text-color)' }}
            >
              $
            </span>
            <input
              type="number"
              className="w-full px-3 py-2 rounded"
              style={{
                backgroundColor: 'var(--bg-item)',
                color: 'var(--text-color)',
                border: '1px solid var(--button-bg-color)',
              }}
              value={customPrice === null ? '' : customPrice} // แสดงค่าว่างหาก customPrice เป็น null
              onChange={(e) => setCustomPrice(e.target.value === '' ? null : Number(e.target.value))} // ตั้งค่า customPrice เป็น null หากค่าว่าง
              placeholder={currentPrice.toString()} // แสดง currentPrice เป็น placeholder
            />
          </div></>
      )}

      {/* ตัวเลือกใส่ % เป้าหมาย */}
      {usePercentage && (
        <div className="mb-4">
          <label className="block mb-2 text-sm" style={{ color: 'var(--text-color)' }}>
            ใส่ % เป้าหมายที่ต้องการ
          </label>
          <div className="flex items-center mb-4">
            <input
              type="number"
              className="w-full px-3 py-2 rounded"
              style={{
                backgroundColor: 'var(--bg-item)',
                color: 'var(--text-color)',
                border: '1px solid var(--button-bg-color)',
              }}
              value={customPercentage || ''}
              onChange={(e) => setCustomPercentage(e.target.value === '' ? null : Number(e.target.value))} // อัปเดต customPercentage
              placeholder={`เปอร์เซนต์ต้องไม่ต่ำกว่า ${calculatedCurrentRatio.toFixed(2)}%`}
            />
          </div>
          {/* แสดงข้อความตามเงื่อนไข */}
          {customPercentage !== null && customPercentage >= calculatedCurrentRatio && (
            <p className="mt-2 text-sm" style={{ color: 'green' }}>
              สัดส่วนที่กรอกเหมาะสม
            </p>
          )}
          {customPercentage !== null && customPercentage < calculatedCurrentRatio && (
            <p className="mt-2 text-sm" style={{ color: 'red' }}>
              กรุณากรอกสัดส่วนให้มากกว่า {calculatedCurrentRatio.toFixed(2)}%
            </p>
          )}
        </div>
      )}
      {/* ตัวเลือกใส่ราคาเป้าหมาย */}
      {useTargetPrice && (
        <div className="mb-4">
          <label className="block mb-2 text-sm" style={{ color: 'var(--text-color)' }}>
            ใส่ค่าเฉลี่ยใหม่ที่อยากได้ <span style={{ color: 'var(--yellow-text-color)' }}>(USD)</span>
          </label>
          <div className="flex items-center mb-4">
            <span
              className="mr-2 font-bold"
              style={{ color: 'var(--text-color)' }}
            >
              $
            </span>
            <input
              type="number"
              className="w-full px-3 py-2 rounded"
              style={{
                backgroundColor: 'var(--bg-item)',
                color: 'var(--text-color)',
                border: '1px solid var(--button-bg-color)',
              }}
              value={targetPrice || ''}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                setTargetPrice(isNaN(value) ? null : value);
              }}
              placeholder={`กรอกค่าเฉลี่ยใหม่ที่อยากได้`} />
          </div>
          {targetPrice !== null && targetPrice !== undefined && targetPrice !== 0 &&
            ((effectivePrice < (initialAvgPrice ?? 0) && (targetPrice < effectivePrice || targetPrice > (initialAvgPrice ?? 0))) ||
              (effectivePrice > (initialAvgPrice ?? 0) && (targetPrice < (initialAvgPrice ?? 0) || targetPrice > effectivePrice))) && (
              <p
                className="mt-2 text-sm"
                style={{ color: 'red' }}
              >
                ราคาในตลาดไม่สอดคล้องกับค่าเฉลี่ยใหม่ที่ต้องการ  <span
                  className="mt-2 text-sm underline cursor-pointer hover:font-bold"
                  style={{ color: 'var(--text-color)' }}
                  onClick={() => {
                    setUseCustomPrice(true); // ติ๊ก "กำหนดราคาเอง"
                    if (targetPrice !== null) {
                      // คริปโตราคาต่ำกว่า $1 ได้ ลบ $1 จะติดลบ → ใช้ต่ำกว่าเป้า 1% แทน
                      setCustomPrice(isCrypto ? targetPrice * 0.99 : targetPrice - 1);
                    }
                  }}
                >
                  กรุณากำหนดราคาเอง</span>
              </p>
            )}
        </div>
      )}
      {!useTargetPrice && !usePercentage && (
        <>
          <label
            className="block mb-2 text-sm"
            style={{ color: 'var(--text-color)' }}
          >
            ใส่เงินลงทุนเพิ่ม <span style={{ color: 'var(--yellow-text-color)' }}>(USD)</span>
          </label>
          <div className="flex items-center mb-4">
            <span
              className="mr-2 font-bold"
              style={{ color: 'var(--text-color)' }}
            >
              $
            </span>
            <input
              type="number"
              className="w-full px-3 py-2 rounded"
              style={{
                backgroundColor: 'var(--bg-item)',
                color: 'var(--text-color)',
                border: '1px solid var(--button-bg-color)',
              }}
              value={invest || ''}
              onChange={(e) => setInvest(Number(e.target.value))}
              placeholder="0"
            />
          </div>
        </>
      )}

      <div className="rounded-lg p-4">

        <>
          <div className="flex justify-between mb-2">
            <span className="text-sm" style={{ color: 'var(--text-color)' }}>
              ต้นทุนเฉลี่ยใหม่:
            </span>
            <span
              className="font-bold text-lg"
              style={{ color: 'var(--yellow-text-color)' }}
            >
              ${usd(newAvgPrice)}
            </span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-sm" style={{ color: 'var(--text-color)' }}>
              จำนวน{unit}ทั้งหมด (ใหม่):
            </span>
            <span
              className="font-bold"
              style={{ color: 'var(--text-color)' }}
            >
              {qty(newShares)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm" style={{ color: 'var(--text-color)' }}>
              ต้นทุนรวม:
            </span>
            <span
              className="font-bold"
              style={{ color: 'var(--text-color)' }}
            >
              ${newCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          {usePercentage && (
            <>
              <div className="flex justify-between mt-2">
                <span className="text-sm" style={{ color: 'var(--text-color)' }}>
                  จำนวน{unit}ที่ต้องซื้อ:
                </span>
                <span className="font-bold" style={{ color: 'var(--text-color)' }}>
                  {qty(requiredShares, 4)}
                </span>
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-sm" style={{ color: 'var(--text-color)' }}>
                  เงินที่ต้องใช้:
                </span>
                <span className="font-bold" style={{ color: 'var(--text-color)' }}>
                  ${requiredInvestment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </>
          )
          }
          {useTargetPrice && (
            <>
              <div className="flex justify-between mt-2">
                <span className="text-sm" style={{ color: 'var(--text-color)' }}>
                  จำนวน{unit}ที่ต้องซื้อ:
                </span>
                <span className="font-bold" style={{ color: 'var(--text-color)' }}>
                  {qty(requiredShares, 4)}
                </span>
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-sm" style={{ color: 'var(--text-color)' }}>
                  เงินที่ต้องใช้:
                </span>
                <span className="font-bold" style={{ color: 'var(--text-color)' }}>
                  ${requiredInvestment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </>
          )
          }
        </>


      </div>
      <button
        className="mt-4 w-full bg-blue-500 hover:bg-blue-600 text-black font-bold py-2 px-4 rounded-lg"
        onClick={() => setShowPopup(true)}
      >
        คำนวนตาม "ไม้ที่ลงทุนบ่อย"
      </button>
      <AverageCalculatePopup
        open={showPopup}
        onClose={() => setShowPopup(false)}
        symbol={symbol}
        currentPrice={effectivePrice}
        currentShares={currentShares}
        currentCost={currentCost}
        useCustomPrice={useCustomPrice}
        customPrice={customPrice}
        totalValueInSubPort={totalValueInSubPort}
        targetRatio={currentExpectRatio}
      />
    </div>
  );
};

export default AverageCalculate;